import { hasOrderStatusHash, maskOrderStatusLinks } from '~/utils/orderStatusLinks';

type YmFunction = (...args: any[]) => void;

const INIT_METHOD = 'init';
const HIT_METHOD = 'hit';

/**
 * Приватные ссылки на заказ (/order-status/<hash>) не должны попадать
 * в Яндекс.Метрику и вебвизор: хэш — это токен доступа к заказу.
 *
 * Модуль Метрики сам вызывает глобальный ym(...) для инициализации счётчика и
 * для каждого перехода, поэтому оборачиваем window.ym: маскируем хэш в
 * аргументах, а если сессия началась с приватной ссылки — просим Метрику не
 * отправлять первый хит (defer) и не записывать вебвизор.
 */
export default defineNuxtPlugin(() => {
  if (import.meta.server || typeof window === 'undefined') {
    return;
  }

  const isPrivateEntry = hasOrderStatusHash(window.location.pathname);

  const privacyInitOptions = (options: unknown) => {
    if (!isPrivateEntry || !options || typeof options !== 'object') {
      return options;
    }

    return { ...options, defer: true, webvisor: false };
  };

  const maskCall = (args: unknown[]) => {
    const [counterId, method, payload, options] = args;

    if (method === HIT_METHOD) {
      return [
        counterId,
        method,
        maskOrderStatusLinks(payload as string),
        options && typeof options === 'object'
          ? { ...options, referer: maskOrderStatusLinks((options as any).referer) }
          : options,
        ...args.slice(4)
      ];
    }

    if (method === INIT_METHOD) {
      return [counterId, method, privacyInitOptions(payload), ...args.slice(3)];
    }

    return args;
  };

  let currentYm: YmFunction | undefined = (window as any).ym;

  const forward = (thisArg: unknown, rawArgs: ArrayLike<unknown>) => {
    if (typeof currentYm !== 'function') {
      return;
    }

    const args: unknown[] = Array.prototype.slice.call(rawArgs);
    currentYm.apply(thisArg, maskCall(args) as any);
  };

  // Прокси сохраняет свойства ym (очередь вызовов .a и метку .l), которые
  // использует загружаемый tag.js, но подменяет аргументы при вызове.
  const ymProxy = new Proxy(function () {} as unknown as YmFunction, {
    apply: (_target, thisArg, args) => forward(thisArg, args),
    get: (_target, property) => (currentYm as any)?.[property],
    set: (_target, property, value) => {
      if (currentYm) {
        (currentYm as any)[property] = value;
      }

      return true;
    }
  });

  // Модуль мог успеть поставить init в очередь до того, как мы обернули ym.
  const patchQueuedInit = () => {
    const queue = currentYm && (currentYm as any).a;

    if (!Array.isArray(queue)) {
      return;
    }

    queue.forEach((call: any) => {
      if (call && call[1] === INIT_METHOD) {
        call[2] = privacyInitOptions(call[2]);
      }
    });
  };

  try {
    Object.defineProperty(window, 'ym', {
      configurable: true,
      get: () => (currentYm ? ymProxy : undefined),
      set: (value: YmFunction) => {
        // Самоприсваивание через прокси (window.ym = window.ym) ломать нельзя.
        if (!value || value === ymProxy) {
          return;
        }

        // tag.js заменяет ym своим обработчиком — переносим накопленную очередь.
        if (currentYm && typeof value === 'function') {
          if ((currentYm as any).a && !(value as any).a) {
            (value as any).a = (currentYm as any).a;
          }
          if ((currentYm as any).l && !(value as any).l) {
            (value as any).l = (currentYm as any).l;
          }
        }

        currentYm = value;
        patchQueuedInit();
      }
    });
  } catch (error) {
    // Маскировка — защитная мера: если обернуть ym не удалось, аналитика важнее.
    console.warn('[metrika-private-links] Не удалось замаскировать приватные ссылки.', error);
  }
});
